// The one place that turns a failed LUD API call into a structured error the UI can act on.
//
// It reads the backend contract (lud-backend/errors.js):  { success: false, code, message, error, details?, requestId? }
// and the shapes that existed before it (`{ error }`, `{ errors }`, `{ message }`, `{ type: "error" }`), plus failures
// that never reached the server: network, offline, timeout, cancellation. The type of an error is decided by the HTTP
// status and the stable `code`, never by parsing the English message.
//
//   kind          when                               presentation (see presentError.js)
//   aborted       the caller cancelled the request   nothing
//   network       fetch threw (DNS, CORS, offline)   global notice
//   offline       navigator.onLine === false         global notice
//   timeout       our AbortController timer fired    global notice
//   unauthorized  401                                session flow (responseGuard / SessionNotice), never a toast
//   forbidden     403                                notice via responseGuard; never a sign-out
//   validation    400 / 422 (and VALIDATION_* codes) inline next to the form
//   not_found     404                                page state
//   conflict      409                                inline / action feedback
//   too_large     413                                inline
//   rate_limited  429                                global notice
//   unavailable   502 / 503 / 504                    global notice
//   server        other 5xx                          global notice
//   invalid       2xx/4xx body we cannot use         global notice

import { ERROR_MESSAGES, KIND_FALLBACK } from "./errorMessages";

const CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,63}$/;
const VALIDATION_CODES = new Set(["VALIDATION_ERROR", "VALIDATION_FAILED", "INVALID_PAYLOAD", "INVALID_ID", "INVALID_QUERY"]);

const kindOfStatus = (status) => {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404 || status === 410) return "not_found";
  if (status === 408) return "timeout";
  if (status === 409) return "conflict";
  if (status === 413) return "too_large";
  if (status === 429) return "rate_limited";
  if (status === 400 || status === 422) return "validation";
  if (status === 502 || status === 503 || status === 504) return "unavailable";
  if (status >= 500) return "server";
  if (status >= 400) return "validation";
  return "invalid";
};

const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;
const textOrEmpty = (value) => (typeof value === "string" ? value : "");

/**
 * @param {{ status?: number, data?: any, aborted?: boolean, timedOut?: boolean, cause?: unknown }} input
 * @returns {{ kind: string, code: string, status: number, serverMessage: string, details?: object, requestId?: string,
 *             retryable: boolean, aborted: boolean }}
 */
export function normalizeApiError({ status = 0, data = null, aborted = false, timedOut = false, cause } = {}) {
  if (aborted && !timedOut) return { kind: "aborted", code: "ABORTED", status: 0, serverMessage: "", retryable: false, aborted: true };
  if (timedOut) return { kind: "timeout", code: "REQUEST_TIMEOUT", status: 0, serverMessage: "", retryable: true, aborted: false };
  if (!status) {
    const offline = isOffline();
    return { kind: offline ? "offline" : "network", code: offline ? "OFFLINE" : "NETWORK_ERROR", status: 0, serverMessage: "", retryable: true, aborted: false, cause };
  }

  const body = data && typeof data === "object" ? data : {};
  const code = CODE_PATTERN.test(body.code || "") ? body.code : null;
  let kind = status >= 200 && status < 300 ? "invalid" : kindOfStatus(status);
  // A validation code makes a 4xx a validation error; a 5xx stays a server failure whatever its code (the report validation
  // route answers an internal failure as 500 VALIDATION_FAILED).
  if (code && VALIDATION_CODES.has(code) && status < 500) kind = "validation";
  const fallbackCode = KIND_FALLBACK[kind] || "SERVER_ERROR";
  return {
    kind,
    code: code || fallbackCode,
    knownCode: Boolean(code && ERROR_MESSAGES[code]),
    status,
    // The server's own (English) message: shown only where no translation exists, and never for 5xx.
    serverMessage: status < 500 ? textOrEmpty(body.message) || textOrEmpty(body.error) || textOrEmpty(body.errors) : "",
    details: body.details && typeof body.details === "object" ? body.details : undefined,
    requestId: typeof body.requestId === "string" ? body.requestId : undefined,
    retryable: kind === "network" || kind === "timeout" || kind === "rate_limited" || kind === "unavailable" || kind === "server",
    aborted: false,
  };
}

// From a thrown value (fetch rejection, an Error with `status` from an API helper, or a normalized error).
export function normalizeThrown(error) {
  if (error && typeof error === "object" && typeof error.kind === "string" && "retryable" in error) return error;
  if (error && error.apiError) return error.apiError;
  if (error && error.name === "AbortError") return normalizeApiError({ aborted: true });
  if (error && error.name === "TimeoutError") return normalizeApiError({ timedOut: true });
  // `response.json()` on an HTML / empty body (a proxy error page): the server answered, but nothing usable.
  if (error instanceof SyntaxError) return { kind: "invalid", code: "INVALID_RESPONSE", status: 0, serverMessage: "", retryable: true, aborted: false };
  if (error && Number.isInteger(error.status) && error.status > 0) {
    return normalizeApiError({ status: error.status, data: { code: error.code, message: error.message, details: error.details } });
  }
  return normalizeApiError({ status: 0, cause: error });
}

// Fills `{name}` placeholders from details with plain text (React escapes it again when rendering).
// `{name}` is a placeholder; `{{name}}` is literal text (template syntax shown to the user). Returns null when a placeholder
// has no usable value, so the caller can fall back instead of showing a sentence with a gap.
// (No lookbehind: older Safari cannot parse it.)
const PLACEHOLDER = /\{\{\w+\}\}|\{(\w+)\}/g;
const fill = (template, details) => {
  let missing = false;
  const text = template.replace(PLACEHOLDER, (match, key) => {
    if (key === undefined) return match;
    const value = details && details[key];
    if (value === undefined || value === null || typeof value === "object" || String(value).trim() === "") {
      missing = true;
      return "";
    }
    return String(value).slice(0, 120);
  });
  return missing ? null : text;
};

/**
 * The text to show for an error in the given language. Known codes use the catalog; an unknown code falls back to the
 * kind's text. In English a safe 4xx server message is preferred over the generic fallback (it is the domain's own
 * explanation); Arabic users always get catalog text. Raw codes are never returned. A catalog text whose placeholder has no
 * value in `details` is skipped the same way (English: the server's 4xx message; otherwise the kind's text).
 */
export function errorText(error, lang = "ar") {
  const language = lang === "en" ? "en" : "ar";
  const normalized = error && typeof error === "object" && "kind" in error ? error : normalizeThrown(error);
  // A client-side code on a 5xx answer would blame the user's input: such an answer gets the server-failure text instead.
  const misleading = normalized.status >= 500 && VALIDATION_CODES.has(normalized.code);
  const entry = misleading ? null : ERROR_MESSAGES[normalized.code];
  const filled = entry ? fill(entry[language] || entry.en, normalized.details) : null;
  if (filled) return filled;
  if (language === "en" && normalized.serverMessage && normalized.status && normalized.status < 500) return normalized.serverMessage;
  const fallback = ERROR_MESSAGES[KIND_FALLBACK[normalized.kind] || "SERVER_ERROR"];
  return fallback[language];
}

// Field-level messages from `details.fields` ([{ field, kind }]) for inline form errors.
export function fieldErrors(error) {
  const fields = error && error.details && Array.isArray(error.details.fields) ? error.details.fields : [];
  return fields.reduce((map, item) => {
    if (item && typeof item.field === "string") map[item.field] = item.kind || "invalid";
    return map;
  }, {});
}
